import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

const StyledSection = styled.section`
  position: relative;
  color: ${(props) => props.theme.colors[props.color]};
  z-index: 10;
  padding: 2rem 2rem 4rem;

  ${(props) =>
    props.limit &&
    `
    max-width: 120rem;
    margin: auto;
  `}

  &:before {
    content: "";
    display: block;
    position: absolute;
    z-index: 7;
    background: ${(props) => props.theme.colors[props.bg]};
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: ${(props) => (props.hasBgImg ? 0.5 : 1)};
  }

  a {
    color: ${(props) => props.theme.colors[props.linkColor]};
  }

  ${(props) => props.theme.media.tablet_landscape_up`
    padding: 4rem 0 4rem;

    & + & {
      padding-bottom: 8rem;
    }
  `}
`

const StyledPreContent = styled.div`
  z-index: 10;
  position: relative;

  ${(props) => props.theme.media.tablet_landscape_up`
    width: 90%;
    margin: 0 auto;
    max-width: 60rem;
    text-align: ${props.align !== null ? props.align : "left"};
  `}
`

const StyledAppendContent = styled(StyledPreContent)``

const StyledLeftContent = styled.div`
  z-index: 10;
  position: relative;
  text-align: left;

  ${(props) => props.theme.media.tablet_landscape_up`
    h2, h3 {
      margin-top: 0;
    }
  `}
`

const StyledRightContent = styled.div`
  text-align: left;
  z-index: 10;
  position: relative;
  text-align: left;
  h2 {
    margin-top: 0;
  }
`

const StyledContent = styled.div`
  z-index: 10;
  position: relative;
  ${(props) => props.textAlign && `text-align: ${props.textAlign}`};

  ${(props) =>
    (props.left || props.right) &&
    `
    display: grid;
    grid-template-rows: 1fr;
    grid-row-gap: 2rem;
    grid-auto-flow: row;
  `}

  ${(props) => props.theme.media.tablet_landscape_up`
    width: 90%;
    margin: 0 auto;
    max-width: 120rem;

    p:first-child {
      // margin-top: 0.5em;
    }

    ${
      (props.right || props.left) &&
      `
      grid-template-columns: 1fr;
      grid-column-gap: 3%;
      grid-auto-flow: column;
    `
    }

    ${
      props.left &&
      props.right &&
      `
      grid-template-columns: 1fr 1fr;
      grid-column-gap: 3%;
      grid-auto-flow: column;
    `
    }

    ${
      props.left &&
      props.hasChildren &&
      `
      grid-template-columns: 1fr 2fr;
      grid-column-gap: 3%;
      grid-auto-flow: column;
    `
    }

    ${
      props.right &&
      props.hasChildren &&
      `
      grid-template-columns: 2fr 1fr;
      grid-column-gap: 3%;
      grid-auto-flow: column;
    `
    }

    ${
      props.left &&
      props.right &&
      props.hasChildren &&
      `
      grid-template-columns: 1fr 1fr 1fr;
      grid-column-gap: 3%;
      grid-auto-flow: column;
    `
    }
  `}

  > p {
    max-width: 60rem;
  }
`

const StyledBgImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
`

function Section({
  prepend,
  append,
  left,
  right,
  textAlign,
  align,
  color,
  children,
  bgImg,
  limit,
  ...props
}) {
  return (
    <StyledSection limit={limit} hasBgImg={!!bgImg} color={color} {...props}>
      {prepend && <StyledPreContent align={align}>{prepend}</StyledPreContent>}
      <StyledContent
        left={!!left}
        right={!!right}
        hasChildren={children !== null}
        textAlign={textAlign}
      >
        {left && <StyledLeftContent>{left}</StyledLeftContent>}
        {children && children}
        {right && <StyledRightContent>{right}</StyledRightContent>}
      </StyledContent>
      {append && (
        <StyledAppendContent align={align}>{append}</StyledAppendContent>
      )}
      {bgImg && <StyledBgImage>{bgImg}</StyledBgImage>}
    </StyledSection>
  )
}

export default Section

Section.defaultProps = {
  bg: null,
  color: null,
  align: null,
  children: null,
}

Section.propTypes = {
  children: PropTypes.node,
  bg: PropTypes.oneOf([
    "primary",
    "gray",
    "secondary",
    "white",
    "cherry",
    "ice",
    "plush",
    "tealDark",
  ]),
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "white",
    "cherry",
    "plush",
    "ice",
    "tealDark",
  ]),
  align: PropTypes.oneOf(["center", "right"]),
}
